<script setup lang="ts">
import { INQUIRY_ITEM, INQUIRY_STATUS } from '@/types'
import { formatCurrency, getShortName } from '@/utils/formatters'
import {
  matchHighlightedInquiryStatus,
  matchInquiryItemShowType,
  showFormattedInquiryCreatedAt,
} from '@/utils/matchers'
import { parseFormFields } from '@/utils/parsers'
import { set, format, parseISO } from 'date-fns'
import { onLongPress } from '@vueuse/core'
const inquiriesStore = useInquiriesStore()
const appStore = useAppStore()

const props = defineProps<INQUIRY_ITEM>()
const item = ref<HTMLElement>()

const emits = defineEmits(['onLongPress'])

function onLongPressCallbackHook() {
  emits('onLongPress', props)
}

onLongPress(item, onLongPressCallbackHook, {
  modifiers: {
    prevent: true,
  },
})

const hasMessages = computed(() => {
  return props.get_num_of_unread_emails > 0
})

const isHighlighted = computed(() => {
  return (
    matchHighlightedInquiryStatus(
      (props.status ||
        props.pick_up_order_status ||
        props.delivery_order_status) as INQUIRY_STATUS,
    ) || hasMessages.value
  )
})

const jobPosition = computed(() => {
  if (props.is_job && props.form_fields) {
    const parsedFields = parseFormFields(props.form_fields)
    return parsedFields.selected_positions
      ? parsedFields.selected_positions.join(', ')
      : ''
  }
})

const formattedDate = computed(() => {
  if (showFormattedInquiryCreatedAt(props.type)) {
    return format(parseISO(props.created_at), "iii, M/d/yyyy 'at' h:mm a")
  }

  return format(
    set(new Date(), {
      year: props.year!,
      month: props.month! - 1,
      date: props.day!,
      hours: props.hour!,
      minutes: props.min!,
      seconds: 0,
    }),
    "iii M/d/yyyy 'at' h:mm a",
  )
})

const isReservation = computed(() => {
  return props.type === 'Reservation'
})

const isJob = computed(() => {
  return props.is_job
})

const isOrder = computed(() => {
  return props.is_order
})

const isParty = computed(() => {
  return props.type === 'Private Party'
})

const isCatering = computed(() => {
  return props.type === 'Catering'
})

const showHandlingIcon = computed(() => {
  return isParty.value && appStore.aiCoordinatorActive
})

const isAiHandling = computed(() => {
  return (
    props.ai_coordinator_handling !== 'off' &&
    !['confirmed', 'cancelled'].includes(props.status ?? '')
  )
})

inquiriesStore.temporaryDocuments = []
inquiriesStore.attachments = []
</script>

<template>
  <div ref="item" class="flex gap-2 border-b border-gray-200 p-3">
    <!-- icon and main content holder-->
    <div class="flex w-[60%] items-start gap-2">
      <!-- icon -->
      <InquiryTypeIcon :type="props.type" :is-highlighted="isHighlighted" />
      <!-- main content -->
      <div class="flex flex-col gap-1">
        <p
          class="font-bold capitalize text-slate-800"
          :class="{ 'font-medium': !isHighlighted }"
        >
          {{ matchInquiryItemShowType(props.type) }}
        </p>
        <div class="text-slate-800" :class="{ 'font-medium': isHighlighted }">
          <p class="flex items-center gap-1.5">
            <template v-if="showHandlingIcon">
              <AppiconAiSuggestedResponse
                v-if="isAiHandling"
                class="size-4 shrink-0"
              />
              <AppiconAiUserHandling v-else class="size-4 shrink-0" />
            </template>
             {{ props.name ? getShortName(props.name) : '/' }}
          </p>
          <div class="text-clip">
            <p v-if="isJob" class="max-w-52 truncate">{{ jobPosition }}</p>
            <template v-if="isParty || isReservation || isCatering">
              <span>{{ props.size }}</span>
            </template>
            <template v-if="isParty || isCatering">
              <span v-if="props.party_type">, {{ props.party_type }}</span>
            </template>
            <template v-if="isOrder && props.order.length">
              <p>{{ formatCurrency(props.order[0].total) }}</p>
            </template>
          </div>
          <p class="mt-1 text-sm font-normal text-slate-600">
            {{ formattedDate }}
          </p>
        </div>
      </div>
    </div>
    <!-- inquiry status and time -->
    <div class="ml-auto w-[40%]">
      <InquiryStatus v-bind="props" />
    </div>
  </div>
</template>
